import "../styles/message-bubble/index.css";

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {
    memo,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import arePropsEqual from "react-fast-compare";
import stringHash from "string-hash";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {styles as typographyStyles} from "@khanacademy/wonder-blocks-typography";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    getDefaultPersonaForProfile,
    useGuideAudioController,
    useSendChatMessage,
} from "@khan/guide-core";
import {
    getGuideActivitySlug,
    isTutorMeActivity,
    maybeIOS,
} from "@khan/guide-core-helpers";
import {GuideStateApi, useGuideState} from "@khan/guide-state";
import {
    ConversationEntryClassification,
    type ActionPromiseCallbacks,
    type ChatInputMetadata,
    type InteractionID,
    type Message,
} from "@khan/guide-types";
import {isExercisePage, type GuideIconReaction} from "@khan/guide-utils";
import {useUserAttributes} from "@khan/shared-core";
import {AppShellContext} from "@khan/shared-core/context";
import {useLocation} from "@khan/urls";

import {Diagram} from "../diagrams/diagram.tsx";
import {getShouldStyleAsStudentMode} from "../get-should-style-as-student-mode.ts";
import {GuideColors} from "../guide-colors.ts";
import {MessageIcon} from "../icons/message-icon.tsx";
import {InputsAndActionBubbles} from "../inputs-and-action-bubbles.tsx";
import {PlaybackButton} from "../playback-button.tsx";
import {StopResponseButton} from "../stop-response-button.tsx";
import {useIOSScrollHack} from "../use-ios-scroll-hack.ts";
import {useTextToSpeechPropsForMessage} from "../use-text-to-speech-props-for-message.ts";

import {ActionBubbleActionStatus} from "./action-bubble-action-status.tsx";
import {FlaggedAnswerText} from "./flagged-answer-text.tsx";
import {getMaybeStatusMessage} from "./get-maybe-status-message.ts";
import {ImagePreviewMessage} from "./image-preview-message.tsx";
import {sharedStyles} from "./message-and-context-bubble-shared-styles.ts";
import {MessageBubbleConfigContext} from "./message-bubble-config.ts";
import {MessageBubbleIncomingStatus} from "./message-bubble-incoming-status.tsx";
import {MessageBubbleOutgoingStatus} from "./message-bubble-outgoing-status.tsx";
import {MessageContent} from "./message-content.tsx";
import {MessageDabToggle} from "./message-dab-toggle.tsx";
import {MessageReactionSelector} from "./message-reaction-selector.tsx";
import {MessageUIToolbar} from "./message-ui-toolbar.tsx";
import {ResponseStatus} from "./response-status.tsx";
import {useActionBubbleHandler} from "./use-action-bubble-handler.ts";
import type {useImagesMap} from "./use-images-map.ts";
import type {OpenModalActionButtonCallback} from "./use-make-action-bubble-handlers.ts";

import arrowClockwiseIcon from "@phosphor-icons/core/bold/arrow-clockwise-bold.svg";

export interface Props {
    imagesMap?: ReturnType<typeof useImagesMap>;
    hadMathCheck?: boolean;
    canBeStopped: boolean;
    isFirstIncomingResponseInResponsesGroup?: boolean;
    isLastIncomingResponseInResponsesGroup?: boolean;
    isMostRecentMessage?: boolean;
    isMostRecentNonSystemMessage?: boolean;
    isInitialMessage?: boolean;
    isSending?: boolean;
    isTypingInChat?: boolean;
    isTypingInCoeditor?: boolean;
    message: Readonly<Message>;
    reaction: GuideIconReaction | null;
    showExpandedIcon?: boolean;
    stopResponse: () => unknown;
    actionBubbleCallbacks: {
        onMessageSend: (
            message: string,
            metadata: ChatInputMetadata,
        ) => unknown;
        openModal: OpenModalActionButtonCallback;
    };
    onLeaveFeedback: (interactionId: InteractionID) => unknown;
    disableDABs?: boolean;
}

/**
 * MessageBubble displays incoming/outgoing messages for interactions with
 * AIGuide.
 */
const MessageBubble = (props: Props): React.ReactElement => {
    const {
        imagesMap,
        hadMathCheck,
        canBeStopped,
        isFirstIncomingResponseInResponsesGroup,
        isLastIncomingResponseInResponsesGroup,
        isMostRecentMessage,
        isMostRecentNonSystemMessage,
        isSending,
        isTypingInChat,
        isTypingInCoeditor,
        message,
        reaction,
        showExpandedIcon,
        stopResponse,
        actionBubbleCallbacks,
        onLeaveFeedback,
        isInitialMessage,
        disableDABs,
    } = props;

    const retryTimeoutSeconds = useGuideState("retryTimeoutSeconds");
    const swagId = useGuideState("swagId");
    const userAsStudent = useGuideState("userAsStudent");
    const isOffline = useGuideState("isOffline");
    const threadId = useGuideState("threadId");
    const {disableReactions} = useContext(MessageBubbleConfigContext);
    const {entrypoint} = useContext(AppShellContext);
    const scrollAnchorRef = useRef<any>();
    const {pathname} = useLocation();
    const {
        requestedInputs,
        actionBubbles,
        contentFlagged,
        interactionId,
        isError,
        isWarning,
        links,
        type,
        actionStatusMap,
    } = message;
    const sendChatMessage = useSendChatMessage();
    const userAttributes = useUserAttributes();
    const isClassroom = entrypoint === "classroom";
    const isOutgoing = type === "outgoing";
    const isIncoming = !isOutgoing;

    const handleShowFeedback = useCallback(() => {
        onLeaveFeedback(interactionId);
    }, [interactionId, onLeaveFeedback]);

    const {isPlaying, isVoiceOn, pause, play, selectedVoice} =
        useGuideAudioController();

    const {
        formattedMessageText,
        hasAudio,
        isOwnAudioPlaying,
        isOwnAudioPlayingFromStream,
    } = useTextToSpeechPropsForMessage(message, !!isMostRecentMessage);

    const showUiButtons =
        isIncoming &&
        isLastIncomingResponseInResponsesGroup &&
        !isError &&
        !message.isGeneratingResponse;

    // We don't want to show the playback button if the message is currently
    // streaming in. This is because we buffer streaming audio and do not
    // want users to prematurely play the audio before it's ready.
    const showPlaybackButton =
        hasAudio && showUiButtons && !isOwnAudioPlayingFromStream;

    // If there is audio playing that is not coming from this message, we
    // will disable the playback button.
    const isPlaybackDisabled = isPlaying && !isOwnAudioPlaying;
    const handlePlayback = useCallback(() => {
        const ownAudioKey: string = interactionId;

        if (!ownAudioKey) {
            return;
        }
        play(ownAudioKey);
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideTextToSpeechReplayed",
            eventSchemaVersion: 1,
            aiGuideInteractionId: interactionId,
            aiGuideNavigationId: GuideStateApi.latest().navigation.id,
            aiGuideThreadId: threadId ?? "",
        });
    }, [play, threadId, interactionId]);

    useIOSScrollHack(
        scrollAnchorRef,
        message,
        isOutgoing,
        isIncoming,
        isMostRecentMessage,
    );

    const handleResendMessage = useCallback(() => {
        const metadata: ChatInputMetadata = {
            containsSpeechToTextContent: false,
            guideVoiceDescriptor: selectedVoice,
            prepopulatedText: null,
            requestUIType: "USER_TYPED",
            requestWithTextToSpeech: isVoiceOn,
            timeToSubmit: 0,
        };
        sendChatMessage(message.text, metadata);
    }, [isVoiceOn, message.text, selectedVoice, sendChatMessage]);

    const [asyncActionSucceeded, setAsyncActionSucceeded] = useState<
        boolean | undefined
    >(undefined);
    const [asyncActionMessage, setAsyncActionMessage] = useState<
        string | undefined
    >(undefined);

    const asyncActionCallbacks: ActionPromiseCallbacks = useMemo(() => {
        return {
            onActionResolved: (message: string) => {
                setAsyncActionMessage(message);
                setAsyncActionSucceeded(true);
            },
            onActionRejected: (message: string) => {
                setAsyncActionMessage(message);
                setAsyncActionSucceeded(false);
            },
        };
    }, [setAsyncActionSucceeded, setAsyncActionMessage]);

    const handleOnActionSelected = useActionBubbleHandler({
        asyncCallbacks: asyncActionCallbacks,
        openModal: actionBubbleCallbacks.openModal,
        // We pass in a sendChatMessage rather than using useSendChatMessage
        // under-the-hood since we wrap the send message method with additional
        // functionality.
        sendChatMessage: actionBubbleCallbacks.onMessageSend,
    });

    // We don't want to show flagged responses from the AI, to the user.
    // `contentFlagged` is true if the previous outgoing message was flagged.
    const interactionFlagged = !isOutgoing && contentFlagged;

    let text = formattedMessageText;

    if (interactionFlagged) {
        // The FlaggedAnswerText component will render instead of this text.
        // However, we still overwrite the text to ensure the AI response is
        // never shown to the user.
        text = "";
    }

    const defaultPersona = userAttributes
        ? getDefaultPersonaForProfile(userAttributes)
        : null;
    const styleAsStudentMode = getShouldStyleAsStudentMode(
        userAsStudent,
        isIncoming,
        pathname,
        defaultPersona,
    );

    // Only render the scroll anchor if this is the last message and we're
    // on an iOS device.
    const renderScrollAnchor = isMostRecentMessage && maybeIOS();

    const dabIdRoot = useId();
    const dabIds: readonly string[] =
        actionBubbles
            ?.filter(({isDynamic}) => isDynamic)
            .map((_, i) => `${dabIdRoot}${i}`) ?? [];

    useEffect(() => {
        // Since interactionId could be an empty string while the message is
        // streaming in, we only publish this event if it's truthy so that we
        // only fire it at most once per interaction.
        if (interactionId && reaction) {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideBotResponseWithReactionReceived",
                eventSchemaVersion: 1,
                aiGuideInteractionId: interactionId,
                aiGuideNavigationId: GuideStateApi.latest().navigation.id,
                aiGuideReactionId: reaction,
                aiGuideThreadId: threadId ?? "",
            });
        }
    }, [threadId, interactionId, reaction]);

    // Set different aspects of message styling based on the message type.
    let botMessageStyle = styles.botMessage;
    switch (message.entryClassification) {
        case ConversationEntryClassification.AssignmentContext:
            botMessageStyle = styles.teacherAssignmentContextMessage;
            break;
    }

    // We only want to show the DAB toggle if the user is a student or learner.
    // Learners are neither teachers nor students. Targeting teachers who are
    // impersonating students adds complexity to the logic.
    const isTeacher = userAttributes?.hasAccessToAIGuideTeacher;
    const isLearnerOrStudent =
        userAsStudent || !isTeacher || (!isTeacher && !userAsStudent);

    /**
     * Generate action bubbles if the streamed response is
     * complete and the user's question was not flagged.
     * Also, no teachers allowed. */
    const shouldRenderDABs = useCallback(
        () =>
            !disableDABs &&
            isLearnerOrStudent &&
            !interactionFlagged &&
            !!isMostRecentMessage &&
            !isInitialMessage &&
            (isTutorMeActivity(getGuideActivitySlug(pathname)) ||
                isExercisePage(pathname)),
        [
            disableDABs,
            interactionFlagged,
            isInitialMessage,
            isLearnerOrStudent,
            isMostRecentMessage,
            pathname,
        ],
    );

    const showIcon =
        isIncoming &&
        !showExpandedIcon &&
        isFirstIncomingResponseInResponsesGroup;

    const statusMessage = hadMathCheck
        ? getMaybeStatusMessage(true, message.text)
        : undefined;
    const textFilteredForThinkingState = reaction === "thinking" ? "" : text;

    return (
        <>
            <li
                data-testid="message-bubble"
                aria-label={isIncoming ? t`Received message` : t`Sent message`}
                className={css(
                    sharedStyles.outerWrapper,
                    isOutgoing
                        ? styles.outgoingMessageWrapper
                        : styles.incomingMessageWrapper,
                )}
            >
                {isIncoming && showExpandedIcon && (
                    <MessageIcon
                        id={swagId}
                        isOffline={isOffline}
                        reaction={reaction}
                        expanded
                        style={[
                            styles.icon,
                            // If it's not the first message in the response
                            // group, we want to hide the icon. We do this
                            // with visibility: hidden instead of display: none
                            // so that the layout doesn't shift around.
                            !isFirstIncomingResponseInResponsesGroup &&
                                styles.visibilityHidden,
                        ]}
                    />
                )}
                <View style={styles.message}>
                    {/* Case: image is being sent to the service, and */}
                    {/* we're waiting for the interactionID to come back */}
                    {!message.interactionId && message.attachedImage && (
                        <ImagePreviewMessage
                            message={{
                                ...message,
                                text: message.attachedImage,
                            }}
                        />
                    )}
                    {/* Case: image was sent and we have the interactionId */}
                    {imagesMap?.has(interactionId) && (
                        <ImagePreviewMessage
                            message={{
                                ...message,
                                // Cast this because we know the key exists
                                text: imagesMap?.get(interactionId) as string,
                            }}
                        />
                    )}
                    {isIncoming && (
                        <ResponseStatus
                            reaction={reaction}
                            statusMessage={statusMessage}
                        >
                            {showIcon ? (
                                <MessageIcon
                                    id={swagId}
                                    isOffline={isOffline}
                                    reaction={reaction}
                                    style={sharedStyles.shrunkenIcon}
                                />
                            ) : null}
                        </ResponseStatus>
                    )}
                    <View
                        style={[
                            sharedStyles.messageBubble,
                            typographyStyles.Body,
                            isOutgoing
                                ? styles.outgoingMessage
                                : styles.incomingMessage,
                            isOutgoing ? styles.userMessage : botMessageStyle,
                            isClassroom &&
                                (isOutgoing
                                    ? styles.classroomUserMessage
                                    : styles.classroomBotMessage),
                            isError && isIncoming ? styles.errorMessage : null,
                            isWarning && isIncoming
                                ? styles.warningMessage
                                : null,
                            styleAsStudentMode &&
                                sharedStyles.botMessageTeacherAsStudentMode,
                        ]}
                        className={
                            isOutgoing &&
                            styles.userMessage.backgroundColor ===
                                GuideColors.darkPurple
                                ? "dark-purple-message"
                                : ""
                        }
                        testId={isOutgoing ? "sent-message" : "ai-message"}
                    >
                        {isOutgoing && isError && (
                            <View style={styles.resendButtonWrapper}>
                                <IconButton
                                    aria-label={t`Resend`}
                                    onClick={handleResendMessage}
                                    icon={arrowClockwiseIcon}
                                    kind="tertiary"
                                    size="xsmall"
                                />
                            </View>
                        )}
                        {interactionFlagged ? (
                            <FlaggedAnswerText
                                hasImage={Boolean(
                                    message.attachedImage ||
                                        imagesMap?.has(message.interactionId),
                                )}
                            />
                        ) : (
                            <>
                                <MessageContent
                                    text={textFilteredForThinkingState}
                                    links={links}
                                    interactionId={interactionId}
                                    shouldShowCursor={
                                        isTypingInChat ||
                                        textFilteredForThinkingState === ""
                                    }
                                    isIncoming={isIncoming}
                                    contentFlagged={contentFlagged}
                                />
                                {message.diagrams?.map((diagramData) => (
                                    <Diagram
                                        key={`${stringHash(diagramData.code)}`}
                                        diagramData={diagramData}
                                    />
                                ))}
                            </>
                        )}
                        {showPlaybackButton && (
                            <View style={styles.playbackButtonWrapper}>
                                <PlaybackButton
                                    disabled={isPlaybackDisabled}
                                    playing={isOwnAudioPlaying}
                                    style={styles.playbackButton}
                                    testId="ai-guide-message-playback-button"
                                    tooltipPlacement="left"
                                    onPause={pause}
                                    onPlay={handlePlayback}
                                />
                            </View>
                        )}
                    </View>
                    <ActionBubbleActionStatus
                        message={asyncActionMessage}
                        didSucceed={asyncActionSucceeded}
                        isError={asyncActionSucceeded === false}
                        style={styles.messageStatus}
                    />
                    <InputsAndActionBubbles
                        // Pass isMostRecentNonSystemMessage so that system messages
                        // do not affect the oldMessageBehavior of action bubbles
                        isMostRecentMessage={isMostRecentNonSystemMessage}
                        requestedInputs={requestedInputs || []}
                        actionBubbles={actionBubbles || []}
                        onActionSelected={handleOnActionSelected}
                        interactionId={interactionId}
                        dabIds={dabIds}
                    />
                    {!contentFlagged && (
                        <View
                            style={[
                                styles.messageStatusWrapper,
                                isIncoming &&
                                    styles.messageIncomingStatusWrapper,
                                isOutgoing &&
                                    styles.messageOutgoingStatusWrapper,
                            ]}
                        >
                            {canBeStopped && (
                                <StopResponseButton
                                    show={canBeStopped}
                                    clickHandler={stopResponse}
                                />
                            )}

                            {isIncoming &&
                                isLastIncomingResponseInResponsesGroup &&
                                actionStatusMap && (
                                    <MessageBubbleIncomingStatus
                                        actionStatusMap={actionStatusMap}
                                        style={styles.messageStatus}
                                    />
                                )}

                            <View>
                                {isOutgoing && !interactionId && (
                                    <MessageBubbleOutgoingStatus
                                        isError={isError}
                                        isSending={isSending}
                                        retryTimeoutSeconds={
                                            retryTimeoutSeconds
                                        }
                                        style={styles.messageStatus}
                                    />
                                )}
                            </View>
                        </View>
                    )}
                    {isIncoming &&
                        isLastIncomingResponseInResponsesGroup &&
                        !message.isGeneratingResponse &&
                        !isTypingInCoeditor &&
                        interactionId && (
                            <MessageUIToolbar>
                                {!isInitialMessage && !disableReactions && (
                                    <MessageReactionSelector
                                        threadId={threadId ?? ""}
                                        interactionId={interactionId}
                                        onShowFeedbackForm={handleShowFeedback}
                                    />
                                )}
                                {shouldRenderDABs() && (
                                    <MessageDabToggle
                                        interactionId={interactionId}
                                        skipEvent={
                                            !!dabIds.length || isInitialMessage
                                        }
                                        dabIds={dabIds}
                                    />
                                )}
                            </MessageUIToolbar>
                        )}
                </View>
            </li>
            {renderScrollAnchor && (
                <span style={styles.anchor} ref={scrollAnchorRef} />
            )}
        </>
    );
};

// IconButton.xsmall is 24px
const RESEND_BUTTON_SIZE = 24;

const styles = StyleSheet.create({
    anchor: {
        // It seems we need a non-zero height to make this hack work.
        height: 1,
    },
    incomingMessage: {
        alignSelf: "flex-start",
        borderStartStartRadius: 0,
    },
    outgoingMessage: {
        alignSelf: "flex-end",
        borderEndEndRadius: 0,
    },
    outgoingMessageWrapper: {
        justifyContent: "flex-end",
        // Left margin is intentionally the same size of resend button + the
        // gap between the resend button and the outgoing message. This makes
        // it so that the resend button (when present) is aligned with the
        // incoming messages.
        marginInlineStart: `calc(${spacing.xxxSmall_4}px + ${RESEND_BUTTON_SIZE}px)`,
    },
    incomingMessageWrapper: {
        justifyContent: "flex-start",
        // Right margin for incoming messages should just match the outgoing
        // left margin.
        marginInlineEnd: `calc(${spacing.xxxSmall_4}px + ${RESEND_BUTTON_SIZE}px)`,
    },
    message: {
        width: "fit-content",
        wordWrap: "break-word", // to wrap words that have really long links
        gap: spacing.xSmall_8,
    },
    botMessage: {
        backgroundColor: GuideColors.peach,
    },
    teacherAssignmentContextMessage: {
        backgroundColor: GuideColors.peach,
        borderLeftColor: GuideColors.darkPurple,
        borderLeftWidth: spacing.xSmall_8,
        borderRadius: 8,
    },
    errorMessage: {
        backgroundColor: semanticColor.core.background.critical.subtle,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: semanticColor.core.border.critical.default,
    },
    messageStatus: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
    },
    messageStatusWrapper: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xSmall_8,
    },
    messageIncomingStatusWrapper: {
        justifyContent: "flex-start",
    },
    messageOutgoingStatusWrapper: {
        justifyContent: "flex-end",
    },
    userMessage: {
        justifyContent: "flex-end",
        color: semanticColor.core.foreground.knockout.default,
        backgroundColor: GuideColors.darkPurple,
    },
    classroomUserMessage: {
        backgroundColor: semanticColor.core.background.instructive.default,
    },
    classroomBotMessage: {
        backgroundColor: semanticColor.core.background.instructive.subtle,
    },
    playbackButton: {
        margin: 0,
    },
    playbackButtonWrapper: {
        position: "absolute",
        top: 0,
        insetInlineStart: "100%",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        height: "100%", // 100% to match the height of the message bubble.
    },
    resendButtonWrapper: {
        position: "absolute",
        insetInlineEnd: "100%",
        top: 0,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        height: "100%", // 100% to match the height of the message bubble.
        marginInlineEnd: spacing.xxxSmall_4,
    },
    icon: {
        width: 56,
        height: 56,
        // Margin should be set such that the message bubble lines up with the
        // chat input.
        marginInlineStart: spacing.large_24,
    },
    warningMessage: {
        backgroundColor: semanticColor.core.background.warning.subtle,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: semanticColor.core.border.warning.default,
    },
    visibilityHidden: {
        visibility: "hidden",
    },
});

const MemoizedMessageBubble = memo(MessageBubble, arePropsEqual);

export {MemoizedMessageBubble as MessageBubble};
